<template>
  <div>
    <v-row>
      <v-col cols="12" sm="4">
        <PayFrequenciesDropdownList
          :label="frequencyLabel"
          :isRequired="frequencyRequired"
          :payFrequency="frequencyId"
          :clearable="false"
          :hideDetails="true"
          @payFrequencyChangeHandler="frequencyChangeHandler">
        </PayFrequenciesDropdownList>
      </v-col>
      <v-col cols="12" sm="8" v-if="frequencyId && frequencyId !== BIWEEKLY">
        <v-menu
          ref="periodPickerMenu"
          v-model="periodPickerMenu"
          :close-on-content-click="false"
          :return-value.sync="monthPickerValue"
          transition="scale-transition"
          offset-y
          max-width="300px"
          min-width="auto"
          :disabled="!frequencyId"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="periodPickerLabel"
              :label="labelValue"
              prepend-inner-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              outlined
              hide-details
              class="mb-7"
            ></v-text-field>
          </template>
          <v-card>
            <v-card-text class="mx-0 px-0">
              <v-row>
                <v-col cols="12">
                  <v-date-picker
                    v-model="monthPickerValueComputed"
                    type="month"
                    no-title
                    scrollable
                  >
                  </v-date-picker>
                </v-col>
              </v-row>
              <v-row v-if="frequencyId !== MONTHLY">
                <v-col cols="12">
                  <v-select
                    class="px-5"
                    :items="periodsList"
                    v-model="selectedPeriodComputed"
                    :label="labelValue"
                    outlined
                    hide-details
                  ></v-select>
                </v-col>
              </v-row>

            </v-card-text>
          </v-card>
        </v-menu>
      </v-col>
      <v-col cols="12" sm="8" v-if="frequencyId && frequencyId === BIWEEKLY">
        <v-row>
          <v-col cols="12" sm="4">
            <YearsProgressiveListDropDown
              :year="year"
              :hideDetails="true"
              @yearChange="biweeklyYearChange"
            ></YearsProgressiveListDropDown>
          </v-col>
          <v-col cols="12" sm="8">
            <v-select
              :items="periodsList"
              v-model="selectedPeriod"
              :label="labelValue"
              outlined
              hide-details
              full-width
              @change="biweeklyPeriodChange"
            ></v-select>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { PayFrequenciesDropdownList } from '@/components/payFrequencies'
import { YearsProgressiveListDropDown } from '@/components/years'
import { MONTHLY, BIWEEKLY } from '@/lib/constants/payFrequencies'

export default {
  components: {
    PayFrequenciesDropdownList,
    YearsProgressiveListDropDown
  },
  props: [
    'frequencyLabel',
    'periodLabel',
    'frequencyId',
    'dateBegin',
    'dateEnd',
    'frequencyRequired',
    'deriveCurrentPeriodOnMount'
  ],
  data () {
    return {
      periodPickerLabel: null,
      monthPickerValue: null,
      monthPickerValueTemp: null,
      periodPickerMenu: false,
      selectedPeriod: null,
      selectedPeriodTemp: null,
      year: new Date().getFullYear(),
      periodInitializationId: 0,
      localPeriodSelection: false,
      MONTHLY,
      BIWEEKLY
    }
  },
  watch: {
    frequencyId: 'setPeriodFromDates',
    dateBegin: 'setPeriodFromDates',
    dateEnd: 'setPeriodFromDates',
    async monthPickerValueTemp (newValue) {
      if (newValue) {
        let temp = newValue.split('-')
        this.year = temp[0]
        let month = temp[1]
        if (this.year && month && this.frequencyId) {
          let filters = { 'year': this.year, 'month': month, 'periodicity': this.frequencyId }
          await this.getPeriods(filters)
          if (this.frequencyId === MONTHLY) {
            this.selectedPeriodTemp = 0
            await this.acceptButtonHandler()
          } else {
            this.selectedPeriodTemp = -1
          }
        }
      }
    },
    async periodPickerMenu (newValue) {
      if (!newValue && (this.selectedPeriodTemp !== null || this.monthPickerValueTemp !== null)) {
        this.selectedPeriodTemp = null
        this.monthPickerValueTemp = null
        if (this.monthPickerValue && this.frequencyId && this.frequencyId !== MONTHLY) {
          let temp = this.monthPickerValue.split('-')
          let filters = { 'year': temp[0], 'month': temp[1], 'periodicity': this.frequencyId }
          await this.getPeriods(filters)
        }
      }
    }
  },
  computed: {
    ...mapFields('periods', ['periodsList']),
    labelValue: {
      get () {
        return this.periodLabel ? this.periodLabel : 'Period'
      }
    },
    monthPickerValueComputed: {
      get () {
        if (this.monthPickerValueTemp !== null) {
          return this.monthPickerValueTemp
        }
        return this.monthPickerValue
      },
      set (newValue) {
        this.monthPickerValueTemp = newValue
      }
    },
    selectedPeriodComputed: {
      get () {
        if (this.selectedPeriodTemp !== null) {
          return this.selectedPeriodTemp
        }
        return this.selectedPeriod
      },
      set (newValue) {
        this.selectedPeriodTemp = newValue
        this.acceptButtonHandler()
      }
    }
  },
  methods: {
    ...mapActions('periods', ['getPeriods']),
    // Mark a picker-originated selection so the parent props it emits do not trigger another fetch.
    // Incrementing the id also makes any older, still-pending initialization stale.
    holdPeriodInitialization () {
      this.periodInitializationId++
      this.localPeriodSelection = true
    },
    // Wait until Vue has processed the parent's echoed prop changes before enabling external sync.
    releasePeriodInitialization () {
      this.$nextTick(() => {
        this.localPeriodSelection = false
      })
    },
    getPeriodDateKey (date) {
      return date ? String(date).slice(0, 10) : null
    },
    findSelectedPeriod () {
      const dateBegin = this.getPeriodDateKey(this.dateBegin)
      const dateEnd = this.getPeriodDateKey(this.dateEnd)

      return this.periodsList.find(period => {
        return this.getPeriodDateKey(period.dateBegin) === dateBegin &&
          this.getPeriodDateKey(period.dateEnd) === dateEnd
      })
    },
    applySelectedPeriod (selectedPeriod) {
      if (!selectedPeriod) {
        return
      }

      this.selectedPeriod = selectedPeriod.value
      this.periodPickerLabel = selectedPeriod.text
      this.monthPickerValue = this.getPeriodDateKey(selectedPeriod.dateBegin).slice(0, 7)
      this.year = parseInt(this.getPeriodDateKey(selectedPeriod.dateBegin).slice(0, 4))
    },
    async setPeriodFromDates () {
      // A local selection already fetched its periods. Ignore the date props echoed back by the
      // parent; this method still runs for date changes that originate outside the picker.
      if (this.localPeriodSelection) {
        return
      }

      if (!this.frequencyId || !this.dateBegin || !this.dateEnd) {
        return
      }

      const initializationId = ++this.periodInitializationId
      const dateBegin = this.getPeriodDateKey(this.dateBegin)
      const [year, month] = dateBegin.split('-')
      const filters = this.frequencyId === BIWEEKLY
        ? { year, periodicity: this.frequencyId }
        : { year, month, periodicity: this.frequencyId }

      await this.getPeriods(filters)
      if (initializationId !== this.periodInitializationId) {
        return
      }

      this.applySelectedPeriod(this.findSelectedPeriod())
    },
    // Re-derives "today's period" for a frequency that's already selected, without going through
    // the dropdown-wired handler below. Skips the frequencyChange emit -- the frequency itself
    // hasn't changed, so the parent shouldn't re-run its own frequency-change side effects.
    deriveCurrentPeriod (frequencyId) {
      this.holdPeriodInitialization()
      return this.applyCurrentPeriodForFrequency(frequencyId)
    },
    async frequencyChangeHandler (newfrequencyId) {
      this.holdPeriodInitialization()
      this.$emit('frequencyChange', newfrequencyId)
      await this.applyCurrentPeriodForFrequency(newfrequencyId)
    },
    async applyCurrentPeriodForFrequency (newfrequencyId) {
      let today = new Date()
      this.year = parseInt(today.getFullYear())
      let month = today.getMonth() + 1
      this.monthPickerValue = this.year + '-' + (month < 10 ? '0' + month : month)
      let filters = newfrequencyId === BIWEEKLY
        ? { 'year': this.year, 'periodicity': newfrequencyId }
        : { 'year': this.year, 'month': month, 'periodicity': newfrequencyId }
      await this.getPeriods(filters)
      let selectedPeriod = this.periodsList.filter((period) => today >= new Date(period.dateBegin) && today <= new Date(period.dateEnd))[0]
      // If the selected period is not found, it means that the current date is not within any of the periods returned by the API.
      // In this case, we will set the selected period to the next month.
      if (!selectedPeriod) {
        let nextMonthDate = new Date()
        nextMonthDate.setMonth(nextMonthDate.getMonth() + 1)
        this.year = nextMonthDate.getFullYear()
        month = nextMonthDate.getMonth() + 1
        filters = { 'year': this.year, 'month': month, 'periodicity': newfrequencyId }
        await this.getPeriods(filters)
        selectedPeriod = this.periodsList.filter((period) => today >= new Date(period.dateBegin) && today <= new Date(period.dateEnd))[0]
        this.monthPickerValue = this.year + '-' + (month < 10 ? '0' + month : month)
      }
      this.$emit('periodChange', selectedPeriod.dateBegin, selectedPeriod.dateEnd)
      this.selectedPeriod = selectedPeriod.value
      this.periodPickerLabel = selectedPeriod.text
      this.releasePeriodInitialization()
    },
    async acceptButtonHandler () {
      this.holdPeriodInitialization()
      this.$emit('periodChange', this.periodsList[this.selectedPeriodTemp].dateBegin, this.periodsList[this.selectedPeriodTemp].dateEnd)
      this.selectedPeriod = this.selectedPeriodTemp
      this.monthPickerValue = this.monthPickerValueTemp || this.monthPickerValue
      this.$refs.periodPickerMenu.save(this.monthPickerValue)
      this.periodPickerLabel = this.periodsList[this.selectedPeriodTemp].text
      this.selectedPeriodTemp = null
      this.monthPickerValueTemp = null
      this.releasePeriodInitialization()
    },

    async biweeklyYearChange (newValue) {
      let today = new Date()
      let filters = { 'year': newValue, 'periodicity': this.frequencyId }
      this.year = newValue
      await this.getPeriods(filters)
      if (parseInt(newValue) === today.getFullYear()) {
        this.selectedPeriod = this.periodsList.filter((period) => today >= new Date(period.dateBegin) && today <= new Date(period.dateEnd))[0]
        this.holdPeriodInitialization()
        this.$emit('periodChange', this.selectedPeriod.dateBegin, this.selectedPeriod.dateEnd)
        this.releasePeriodInitialization()
      } else {
        this.selectedPeriod = null
      }
    },
    biweeklyPeriodChange (newValue) {
      this.holdPeriodInitialization()
      this.$emit('periodChange', this.periodsList[newValue].dateBegin, this.periodsList[newValue].dateEnd)
      this.selectedPeriod = newValue
      this.releasePeriodInitialization()
    }
  },
  mounted () {
    // Opt-in for a parent that mounts this picker with a frequency but no dates and expects a
    // period straight away -- setPeriodFromDates is a no-op without a range to match, so the
    // period would otherwise sit blank until the admin reselected one by hand.
    if (this.deriveCurrentPeriodOnMount && this.frequencyId && (!this.dateBegin || !this.dateEnd)) {
      this.deriveCurrentPeriod(this.frequencyId)
      return
    }

    this.setPeriodFromDates()
  }
}
</script>
